// ---- the letters, as they are drawn ----
// What every spelling game in this house draws its letters from: the script
// (its font, the language the robot voice falls back to, and what counts as a
// letter in it) and the colour a letter is always given. The colour was in
// jiddo/src/data.ts, in among his animals, because he was the only game with a
// tray of round slots and a sky full of chips. It is not about animals and not
// about food — the same ب is the same blue in either game, which is the point:
// a child who learns his letters in one of these learns the same letters in the
// other.
//
// MAXLEN deliberately did NOT come with them. It reads like a member of this
// set and is not — nothing imports it. It is a rule about how long a word may be
// before a tray's slots go too lean to read, scripts/check-words.js reads it
// out of each game's data.ts as text, and a game with shorter words may
// legitimately set it lower. It stays with the roster it constrains.
//
// The script is Arabic until a game says otherwise (setScript), so Jiddo's
// garden and Danny's sofra never say anything about it.
//
// What a "letter" is, is the part that does not carry over. In Arabic it is one
// character: ب is at once a chip, a slot, a clip (L_ب) and a key in NAMES.
// Devanagari writes a syllable as a consonant plus a vowel sign — रो is र and ो —
// and a vowel sign on its own is a dotted circle, not something a child can tap.
// So a Hindi game deals in aksharas, the syllable blocks a reader actually sees,
// and they come from the game's data as a table (`units`) rather than being
// worked out from the string: Intl.Segmenter's idea of where a conjunct ends
// has moved between browser versions, and a word cut differently on two phones
// is worse than one cut by hand. A word the table does not list is one letter
// per character, which is every Arabic word there is.
export interface ScriptCfg{
  font:string;                       // the stack the chips are drawn in (the tray's is the page's CSS)
  lang:string;                       // what the robot voice speaks when a clip is missing (audio.ts)
  units?:Record<string,string[]>;    // a word as the pieces a child taps, where that is not one per character
  // Centre a chip's glyph on its own ink, and shrink it to fit the disc. An
  // akshara can stack a conjunct over a vowel sign (ड्डू) and outgrows a disc
  // sized for one letter; Arabic letters never do, and are placed by the eye
  // instead (chips.ts), so this stays off for them and they draw as they always have.
  box?:boolean;
}
let script:ScriptCfg={font:'"Geeza Pro","Noto Naskh Arabic",sans-serif',lang:'ar-SA'};
export const setScript=(c:ScriptCfg)=>{script=c;faces.clear();fits.clear();};
export const scriptOf=()=>script;
// A word as its letters. The round asks for this every frame (wanted, round.ts),
// so a listed word is the table's own array and an unlisted one is cut once.
const cut=new Map<string,string[]>();
export function unitsOf(w:string){
  const u=script.units?.[w]; if(u)return u;
  let c=cut.get(w); if(!c)cut.set(w,c=[...w]);
  return c;
}
// A word's letters as they are shown: each with the حركة it carries in the
// word — the مَ, the وْ and the bare ز of مَوْز — read off its vowelled form
// (DIA, setVowelled). A letter is still the letter (unitsOf) to everything that
// is not drawing it: its clip, its name, its colour. Where a letter carries no
// mark in the vowelled form, as a long vowel or a word's last letter mostly does
// not, it has none here either, so what DIA says is the whole rule of where a
// mark goes. Arabic only: a Hindi letter is an akshara and already wears its
// sign, and a Hindi DIA is how a word is said (तरबूज़), not how it is written.
// A vowelled form that is not the word with marks on it is ignored, and the
// word is shown bare, rather than the tray spelling something it is not
// (scripts/check-words.js says so first).
const MARK=/[\u064B-\u0652\u0670]/, MARKS=/[\u064B-\u0652\u0670]/g;
let vowelled:Record<string,string>={};
const faces=new Map<string,string[]>();
export const setVowelled=(d:Record<string,string>)=>{vowelled=d;faces.clear();};
export function facesOf(w:string){
  let f=faces.get(w); if(f)return f;
  const u=unitsOf(w), v=vowelled[w];
  f=u;
  if(v&&!script.units?.[w]&&script.lang.startsWith('ar')){
    const m:string[]=[];
    for(const c of v){if(MARK.test(c)&&m.length)m[m.length-1]+=c;else m.push(c);}
    if(m.length===u.length&&m.every((s,i)=>s.replace(MARKS,'')===u[i]))f=m;
  }
  faces.set(w,f); return f;
}
// How much a letter must shrink to sit inside its disc, as a fraction of the
// size it is drawn at (1 when it fits, and always 1 unless the script asks for
// `box`): the chip draws at 150px inside a 256px disc, and its ink may take up
// 170 of that across and 150 down. The tray's slots are the same proportion
// (.62 of the slot), so the one fraction serves both.
let probe:CanvasRenderingContext2D|null=null;
export function inkFit(ch:string){
  if(!script.box)return 1;
  probe??=document.createElement('canvas').getContext('2d')!;
  probe.font='bold 150px '+script.font;
  const m=probe.measureText(ch), h=m.actualBoundingBoxAscent+m.actualBoundingBoxDescent;
  return Math.min(1,170/(m.actualBoundingBoxLeft+m.actualBoundingBoxRight||1),150/(h||1));
}
// Where a letter wearing a حركة sits in its disc. A mark can take a letter
// higher than any bare letter goes (the shadda and fatha over طَّ) or lower
// (the kasra under خِ), and off the edge of the disc. So its ink is measured as
// the chip draws it — at 150px, from the middle of the em, 12px below the disc's
// centre — and given the room the bare letters use: up to 112px above that (أ,
// the tallest, rises exactly that far) and 88 below (the deepest fall 80). A
// face that outgrows it is moved, by as little as gets it in, and drawn smaller
// only when it is taller than the room altogether. k is how much smaller, and dy
// how far down it moves, as a fraction of the size it is drawn at, which is what
// the slot's em is (style.css). A bare letter is never touched.
const UP=112, DOWN=88, fits=new Map<string,{k:number;dy:number}>();
export function markFit(face:string){
  let f=fits.get(face); if(f)return f;
  f={k:1,dy:0};
  if(!script.box&&MARK.test(face)){
    probe??=document.createElement('canvas').getContext('2d')!;
    probe.font='bold 150px '+script.font; probe.textBaseline='middle';
    const m=probe.measureText(face), a=m.actualBoundingBoxAscent, d=m.actualBoundingBoxDescent;
    const k=Math.min(1,(UP+DOWN)/((a+d)||1)), over=a*k-UP, under=d*k-DOWN;
    f={k,dy:(over>0?over:under>0?-under:0)/(150*k)};
  }
  fits.set(face,f); return f;
}
// Six inks, picked by codepoint so a letter keeps its colour everywhere it
// appears — the chip in the sky, the slot it flies into, the tray's dashed ring.
// An akshara takes its consonant's: रो and रा are both र's colour.
const LCOL=['#D85A30','#185FA5','#0F6E56','#993556','#534AB7','#BA7517'];
export const colOf=(ch:string)=>LCOL[ch.charCodeAt(0)%LCOL.length];

// ---- the first letter, in its parts ----
// A sign is whatever rides on a consonant to give it its vowel: a Devanagari
// matra, the anusvara and its kin, or an Arabic حركة. A nukta and a halant are
// not signs — they belong to the consonant, and a conjunct's halant is what
// makes it one. The tracing board (trace.ts) draws a word's first letter in
// these parts, and the pack builder (scripts/build-pack.mjs) lists the clips
// it will say for them, so the cut is made here, once, where nothing of the
// page is needed to make it.
const DEVA_SIGN=/[ऀ-ःऺऻा-ौॎॏॕ-ॗॢॣ]/;
const ARAB_SIGN=/[ً-ٰٟ]/;
const isSign=(c:string)=>DEVA_SIGN.test(c)||ARAB_SIGN.test(c);
// syl is the first letter as it is said (के, مَ), base the consonant alone (क,
// م), sign what is left (े, ـَ). pre is ि, which is written before the
// consonant it follows. word is what the board spells out at the end: the
// word as the tray shows it, an Arabic one with its vowels on.
export interface First{syl:string;base:string;sign:string;pre:boolean;word:string}
export function firstOf(w:string):First{
  const f=facesOf(w), syl=f[0], word=f.join('');
  const c=[...syl]; let j=c.length;
  while(j>1&&isSign(c[j-1]))j--;
  return {syl,base:c.slice(0,j).join(''),sign:c.slice(j).join(''),pre:syl.includes('ि'),word};
}
